import type {
  FinanceDataReliability,
  FinanceRollupStageTotals,
  FinanceRollupSummary,
  ProjectFinanceTimelineDepartmentMovement,
  ProjectFinanceTimelineEvent,
  ProjectFinanceTimelineInsight,
  ProjectFinanceTimelineSnapshot,
  ProjectFinanceTimelineSummary,
  Task,
  TaskFinanceEvent,
  TaskFinancialStage,
  TaskFinancialStageSnapshot,
  TaskFinancialState,
} from "../models/production";

interface ProjectTimelineTaskEntry {
  task: Pick<Task, "id" | "taskType" | "title" | "createdAt" | "updatedAt" | "assignedArtistIds">;
  financialState?: TaskFinancialState;
}

interface BuildProjectFinanceTimelineArgs {
  projectId: string;
  projectName?: string;
  currency?: string;
  projectRollup?: FinanceRollupSummary;
  tasks: ProjectTimelineTaskEntry[];
  projectCreatedAt?: string;
  projectUpdatedAt?: string;
  now?: string;
}

const SNAPSHOT_ORDER: ProjectFinanceTimelineSnapshot["snapshotType"][] = [
  "initial_bid_baseline",
  "post_assignment_forecast",
  "live_in_progress_forecast",
  "final_actual",
];

function roundCurrency(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 100) / 100;
}

function roundMargin(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 10) / 10;
}

function calculateMarginPct(grossProfit?: number, bidValue?: number) {
  if (typeof grossProfit !== "number" || typeof bidValue !== "number" || !Number.isFinite(grossProfit) || !Number.isFinite(bidValue) || bidValue <= 0) {
    return undefined;
  }
  return roundMargin((grossProfit / bidValue) * 100);
}

function emptyStageTotals(): FinanceRollupStageTotals {
  return {
    bidValue: 0,
    labourCost: 0,
    grossProfit: 0,
    grossMarginPct: 0,
    taskCount: 0,
  };
}

function sortIsoAscending(a?: string, b?: string) {
  const aValue = a ? new Date(a).getTime() : Number.POSITIVE_INFINITY;
  const bValue = b ? new Date(b).getTime() : Number.POSITIVE_INFINITY;
  return aValue - bValue;
}

function findEarliestDate(values: Array<string | undefined>) {
  return values.filter(Boolean).sort(sortIsoAscending)[0];
}

function findLatestDate(values: Array<string | undefined>) {
  return values
    .filter(Boolean)
    .sort((a, b) => sortIsoAscending(b, a))[0];
}

function getStageSnapshot(state: TaskFinancialState | undefined, stage: TaskFinancialStage): TaskFinancialStageSnapshot | undefined {
  return state?.stageSnapshots?.find((snapshot) => snapshot.stage === stage);
}

function aggregateTaskSnapshots(
  tasks: ProjectTimelineTaskEntry[],
  getSnapshot: (entry: ProjectTimelineTaskEntry) => Pick<TaskFinancialStageSnapshot, "labourCost" | "grossProfit"> | undefined,
  getBidValue?: (entry: ProjectTimelineTaskEntry) => number | undefined
) {
  let bidValue = 0;
  let labourCost = 0;
  let grossProfit = 0;
  let taskCount = 0;

  for (const entry of tasks) {
    const state = entry.financialState;
    const snapshot = getSnapshot(entry);
    const taskBidValue = getBidValue ? getBidValue(entry) : state?.taskBidValueInMoney;
    if (typeof taskBidValue === "number" && Number.isFinite(taskBidValue)) {
      bidValue += taskBidValue;
    }
    if (typeof snapshot?.labourCost === "number" && Number.isFinite(snapshot.labourCost)) {
      labourCost += snapshot.labourCost;
    }
    if (typeof snapshot?.grossProfit === "number" && Number.isFinite(snapshot.grossProfit)) {
      grossProfit += snapshot.grossProfit;
    }
    taskCount += 1;
  }

  return {
    bidValue: roundCurrency(bidValue),
    labourCost: roundCurrency(labourCost),
    grossProfit: roundCurrency(grossProfit),
    grossMarginPct: calculateMarginPct(grossProfit, bidValue),
    taskCount,
  } satisfies FinanceRollupStageTotals;
}

function toStageLabel(snapshotType: ProjectFinanceTimelineSnapshot["snapshotType"]) {
  switch (snapshotType) {
    case "initial_bid_baseline":
      return {
        title: "Initial Bid Baseline",
        helperText: "Original commercial baseline using department-average labour assumptions before staffing or production movement.",
      };
    case "post_assignment_forecast":
      return {
        title: "Post-Assignment Forecast",
        helperText: "Forecast after artist assignment decisions have replaced department-average cost assumptions where possible.",
      };
    case "live_in_progress_forecast":
      return {
        title: "Live In-Progress Forecast",
        helperText: "Operational live view blending actual work to date with remaining assumptions for unfinished tasks.",
      };
    case "final_actual":
      return {
        title: "Final Actual",
        helperText: "Completed-task actuals only. This is useful for hindsight, not as an accounting ledger.",
      };
  }
}

function getStageReliability(
  snapshotType: ProjectFinanceTimelineSnapshot["snapshotType"],
  projectRollup?: FinanceRollupSummary
): FinanceDataReliability | undefined {
  if (!projectRollup) return undefined;
  if (snapshotType === "final_actual") return projectRollup.financeReliability;
  if (snapshotType === "live_in_progress_forecast") return projectRollup.financeReliability;
  return projectRollup.financeReliability === "low" ? "medium" : projectRollup.financeReliability;
}

function summarizeDominantEventLabel(args: {
  assignmentEvents: TaskFinanceEvent[];
  overrunEvents: TaskFinanceEvent[];
  completionEvents: TaskFinanceEvent[];
  timelogEventCount: number;
  snapshotType: ProjectFinanceTimelineSnapshot["snapshotType"];
}) {
  const { assignmentEvents, overrunEvents, completionEvents, timelogEventCount, snapshotType } = args;
  if (snapshotType === "initial_bid_baseline") return "Bid baseline established";
  if (snapshotType === "post_assignment_forecast") {
    return assignmentEvents.length > 0 ? `${assignmentEvents.length} assignment change${assignmentEvents.length === 1 ? "" : "s"}` : "Assignment assumptions applied";
  }
  if (snapshotType === "live_in_progress_forecast") {
    if (overrunEvents.length > 0) return `${overrunEvents.length} over-schedule alert${overrunEvents.length === 1 ? "" : "s"}`;
    if (timelogEventCount > 0) return `${timelogEventCount} timelog-driven update${timelogEventCount === 1 ? "" : "s"}`;
    return "Live production movement";
  }
  return completionEvents.length > 0 ? `${completionEvents.length} completed task${completionEvents.length === 1 ? "" : "s"}` : "Completed work actuals";
}

function aggregateDepartmentMovement(
  tasks: ProjectTimelineTaskEntry[],
  fromStage: TaskFinancialStage,
  toValue: (entry: ProjectTimelineTaskEntry) => number | undefined
) {
  const movementByDepartment = new Map<string, { grossProfitDelta: number; taskCount: number }>();

  for (const entry of tasks) {
    const fromSnapshot = getStageSnapshot(entry.financialState, fromStage);
    const toGrossProfit = toValue(entry);
    const fromGrossProfit = fromSnapshot?.grossProfit;
    if (typeof toGrossProfit !== "number" || !Number.isFinite(toGrossProfit) || typeof fromGrossProfit !== "number" || !Number.isFinite(fromGrossProfit)) {
      continue;
    }

    const key = entry.task.taskType || "unknown";
    const current = movementByDepartment.get(key) || { grossProfitDelta: 0, taskCount: 0 };
    current.grossProfitDelta += toGrossProfit - fromGrossProfit;
    current.taskCount += 1;
    movementByDepartment.set(key, current);
  }

  return Array.from(movementByDepartment.entries())
    .map<ProjectFinanceTimelineDepartmentMovement>(([department, value]) => ({
      department,
      grossProfitDelta: roundCurrency(value.grossProfitDelta),
      taskCount: value.taskCount,
      direction: value.grossProfitDelta < 0 ? "erosion" : value.grossProfitDelta > 0 ? "improvement" : "flat",
    }))
    .sort((a, b) => Math.abs(b.grossProfitDelta || 0) - Math.abs(a.grossProfitDelta || 0));
}

function getAllFinanceEvents(tasks: ProjectTimelineTaskEntry[]) {
  return tasks.flatMap((entry) => entry.financialState?.financeEvents || []);
}

function buildTimelineEvents(tasks: ProjectTimelineTaskEntry[], args: BuildProjectFinanceTimelineArgs): ProjectFinanceTimelineEvent[] {
  const financeEvents = getAllFinanceEvents(tasks);
  const assignmentEvents = financeEvents.filter((event) => event.trigger === "assignment_updated");
  const overrunEvents = financeEvents.filter((event) => event.eventType === "over_schedule_alert");
  const completionEvents = financeEvents.filter((event) => event.eventType === "final_actual_locked");
  const timelogDrivenTasks = tasks.filter((entry) => (entry.financialState?.actualLoggedMinutes || 0) > 0);
  const timelogCoverageRatio =
    typeof args.projectRollup?.totalLoggedMinutes === "number" &&
    typeof args.projectRollup?.totalBidTimeAllocationMinutes === "number" &&
    args.projectRollup.totalBidTimeAllocationMinutes > 0
      ? (args.projectRollup.totalLoggedMinutes / args.projectRollup.totalBidTimeAllocationMinutes) * 100
      : undefined;

  const events: ProjectFinanceTimelineEvent[] = [
    {
      id: `${args.projectId}:created`,
      eventType: "project_created",
      title: "Project created / bid imported",
      helperText: "This is the initial commercial starting point before staffing and delivery start to move margin.",
      occurredAt: args.projectCreatedAt || findEarliestDate(tasks.map((entry) => entry.task.createdAt)) || args.projectUpdatedAt,
      relatedTaskCount: tasks.length,
    },
  ];

  if (assignmentEvents.length > 0) {
    events.push({
      id: `${args.projectId}:assignment`,
      eventType: "assignment_changed",
      title: "Artist assignment decisions changed the forecast",
      helperText: "Assigned artist rates replaced department-average assumptions on part of the project.",
      occurredAt: findLatestDate(assignmentEvents.map((event) => event.createdAt)),
      relatedTaskCount: assignmentEvents.length,
    });
  }

  if (timelogDrivenTasks.length > 0) {
    events.push({
      id: `${args.projectId}:timelog`,
      eventType: "timelog_milestone",
      title: "Live timelog milestone reached",
      helperText:
        typeof timelogCoverageRatio === "number"
          ? `Timelogs now cover about ${Math.round(timelogCoverageRatio)}% of bid allocation across the project.`
          : "Timelogs are now materially influencing the live forecast.",
      occurredAt: findLatestDate(tasks.map((entry) => entry.financialState?.lastComputedAt || entry.task.updatedAt)),
      relatedTaskCount: timelogDrivenTasks.length,
    });
  }

  if (overrunEvents.length > 0) {
    events.push({
      id: `${args.projectId}:overrun`,
      eventType: "task_overrun",
      title: "Task overruns started eroding margin",
      helperText: "One or more tasks have exceeded their bid time allowance.",
      occurredAt: findLatestDate(overrunEvents.map((event) => event.createdAt)),
      relatedTaskCount: overrunEvents.length,
    });
  }

  if (completionEvents.length > 0) {
    events.push({
      id: `${args.projectId}:completed`,
      eventType: "task_completed",
      title: "Final actuals began to lock in",
      helperText: "Completed tasks are now moving from forecasted margin into actual reported results.",
      occurredAt: findLatestDate(completionEvents.map((event) => event.createdAt)),
      relatedTaskCount: completionEvents.length,
    });
  }

  return events.sort((a, b) => sortIsoAscending(a.occurredAt, b.occurredAt));
}

function buildInsights(tasks: ProjectTimelineTaskEntry[], projectRollup?: FinanceRollupSummary, currency = "GBP"): ProjectFinanceTimelineInsight[] {
  const assignmentImpact = tasks.reduce((sum, entry) => {
    const initial = getStageSnapshot(entry.financialState, "initial_bid_forecast")?.grossProfit;
    const assigned = getStageSnapshot(entry.financialState, "assigned_artist_forecast")?.grossProfit;
    if (typeof initial !== "number" || typeof assigned !== "number") return sum;
    return sum + (assigned - initial);
  }, 0);

  const overrunImpact = tasks.reduce((sum, entry) => {
    const state = entry.financialState;
    if (!state) return sum;
    if (state.financialStage !== "in_progress_over_schedule" && !state.overScheduleAlertOpen && (state.varianceVsBidMinutes || 0) <= 0) {
      return sum;
    }
    const assigned = getStageSnapshot(state, "assigned_artist_forecast")?.grossProfit;
    const current = state.actualGrossProfit ?? state.forecastGrossProfit;
    if (typeof assigned !== "number" || typeof current !== "number") return sum;
    return sum + (current - assigned);
  }, 0);

  const lowConfidenceTasks = tasks.filter(
    (entry) => entry.financialState?.missingTimelogCoverage || entry.financialState?.fallbackActualsUsed || entry.financialState?.financeReliability === "low"
  );
  const lowConfidenceBidExposure = lowConfidenceTasks.reduce((sum, entry) => sum + (entry.financialState?.taskBidValueInMoney || 0), 0);

  const departmentMovement = aggregateDepartmentMovement(
    tasks,
    "initial_bid_forecast",
    (entry) => entry.financialState?.actualGrossProfit ?? entry.financialState?.forecastGrossProfit
  )[0];

  return [
    {
      id: "assignment-impact",
      title: "Assignment impact",
      valueLabel: `${assignmentImpact >= 0 ? "+" : ""}${new Intl.NumberFormat("en-US", { style: "currency", currency }).format(assignmentImpact)}`,
      helperText: "Margin movement between the initial bid baseline and the post-assignment forecast.",
      tone: assignmentImpact < 0 ? "negative" : assignmentImpact > 0 ? "positive" : "neutral",
    },
    {
      id: "overrun-impact",
      title: "Overrun impact",
      valueLabel: `${overrunImpact >= 0 ? "+" : ""}${new Intl.NumberFormat("en-US", { style: "currency", currency }).format(overrunImpact)}`,
      helperText: "Margin movement attributable to tasks that have moved over their bid time allocation.",
      tone: overrunImpact < 0 ? "negative" : overrunImpact > 0 ? "positive" : "neutral",
    },
    {
      id: "timelog-confidence",
      title: "Low-confidence timelog exposure",
      valueLabel: `${new Intl.NumberFormat("en-US", { style: "currency", currency }).format(lowConfidenceBidExposure)}`,
      helperText: `${lowConfidenceTasks.length} task${lowConfidenceTasks.length === 1 ? "" : "s"} still rely on incomplete or fallback timelog assumptions.`,
      tone: lowConfidenceTasks.length > 0 ? "warning" : "neutral",
    },
    {
      id: "department-movement",
      title: "Biggest department movement",
      valueLabel: departmentMovement
        ? `${departmentMovement.department} • ${departmentMovement.grossProfitDelta && departmentMovement.grossProfitDelta >= 0 ? "+" : ""}${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
          }).format(departmentMovement?.grossProfitDelta || 0)}`
        : "—",
      helperText: departmentMovement
        ? `${departmentMovement.taskCount} task${departmentMovement.taskCount === 1 ? "" : "s"} contributed the largest gross-profit movement from baseline to the current live view.`
        : "No department movement is available yet.",
      tone:
        !departmentMovement ? "neutral" : departmentMovement.direction === "erosion" ? "negative" : departmentMovement.direction === "improvement" ? "positive" : "neutral",
    },
  ];
}

export function buildProjectFinanceTimeline(args: BuildProjectFinanceTimelineArgs): ProjectFinanceTimelineSummary {
  const now = args.now || new Date().toISOString();
  const tasks = args.tasks || [];
  const financeEvents = getAllFinanceEvents(tasks);
  const assignmentEvents = financeEvents.filter((event) => event.trigger === "assignment_updated");
  const overrunEvents = financeEvents.filter((event) => event.eventType === "over_schedule_alert");
  const completionEvents = financeEvents.filter((event) => event.eventType === "final_actual_locked");
  const timelogDrivenTasks = tasks.filter((entry) => (entry.financialState?.actualLoggedMinutes || 0) > 0);

  const baselineTotals =
    aggregateTaskSnapshots(tasks, (entry) => getStageSnapshot(entry.financialState, "initial_bid_forecast")) || emptyStageTotals();
  const assignmentTotals = aggregateTaskSnapshots(
    tasks,
    (entry) => getStageSnapshot(entry.financialState, "assigned_artist_forecast") || getStageSnapshot(entry.financialState, "initial_bid_forecast")
  );
  const liveTotals =
    args.projectRollup?.blendedLive ||
    aggregateTaskSnapshots(tasks, (entry) => ({
      labourCost: entry.financialState?.actualLabourCost ?? entry.financialState?.forecastLabourCost,
      grossProfit: entry.financialState?.actualGrossProfit ?? entry.financialState?.forecastGrossProfit,
    }));
  const finalTotals =
    args.projectRollup?.finalActual ||
    aggregateTaskSnapshots(
      tasks.filter((entry) => entry.financialState?.financialStage === "final_actual"),
      (entry) => getStageSnapshot(entry.financialState, "final_actual")
    );

  const rawSnapshots: Array<Omit<ProjectFinanceTimelineSnapshot, "deltaFromPreviousGrossProfit" | "deltaFromPreviousMarginPct">> = [
    {
      id: `${args.projectId}:baseline`,
      snapshotType: "initial_bid_baseline",
      ...toStageLabel("initial_bid_baseline"),
      createdAt: args.projectCreatedAt || findEarliestDate(tasks.map((entry) => entry.task.createdAt)) || args.projectUpdatedAt,
      totals: baselineTotals,
      reliability: getStageReliability("initial_bid_baseline", args.projectRollup),
      financialConfidenceScore: args.projectRollup?.financialConfidenceScore,
      relatedTaskCount: tasks.length,
      dominantEventLabel: summarizeDominantEventLabel({ assignmentEvents, overrunEvents, completionEvents, timelogEventCount: timelogDrivenTasks.length, snapshotType: "initial_bid_baseline" }),
      departmentMovements: aggregateDepartmentMovement(tasks, "initial_bid_forecast", (entry) => getStageSnapshot(entry.financialState, "initial_bid_forecast")?.grossProfit).slice(0, 3),
    },
    {
      id: `${args.projectId}:assignment`,
      snapshotType: "post_assignment_forecast",
      ...toStageLabel("post_assignment_forecast"),
      createdAt: findEarliestDate(assignmentEvents.map((event) => event.createdAt)) || findLatestDate(tasks.filter((entry) => (entry.task.assignedArtistIds || []).length > 0).map((entry) => entry.task.updatedAt)) || args.projectUpdatedAt,
      totals: assignmentTotals,
      reliability: getStageReliability("post_assignment_forecast", args.projectRollup),
      financialConfidenceScore: args.projectRollup?.financialConfidenceScore,
      relatedTaskCount: tasks.filter((entry) => (entry.task.assignedArtistIds || []).length > 0).length,
      dominantEventLabel: summarizeDominantEventLabel({ assignmentEvents, overrunEvents, completionEvents, timelogEventCount: timelogDrivenTasks.length, snapshotType: "post_assignment_forecast" }),
      departmentMovements: aggregateDepartmentMovement(tasks, "initial_bid_forecast", (entry) => getStageSnapshot(entry.financialState, "assigned_artist_forecast")?.grossProfit).slice(0, 3),
    },
    {
      id: `${args.projectId}:live`,
      snapshotType: "live_in_progress_forecast",
      ...toStageLabel("live_in_progress_forecast"),
      createdAt:
        findLatestDate([
          ...overrunEvents.map((event) => event.createdAt),
          ...tasks.map((entry) => entry.financialState?.lastComputedAt),
          args.projectRollup?.lastComputedAt,
        ]) || args.projectUpdatedAt,
      totals: liveTotals,
      reliability: getStageReliability("live_in_progress_forecast", args.projectRollup),
      financialConfidenceScore: args.projectRollup?.financialConfidenceScore,
      relatedTaskCount: timelogDrivenTasks.length,
      dominantEventLabel: summarizeDominantEventLabel({ assignmentEvents, overrunEvents, completionEvents, timelogEventCount: timelogDrivenTasks.length, snapshotType: "live_in_progress_forecast" }),
      departmentMovements: aggregateDepartmentMovement(tasks, "assigned_artist_forecast", (entry) => entry.financialState?.actualGrossProfit ?? entry.financialState?.forecastGrossProfit).slice(0, 3),
    },
    {
      id: `${args.projectId}:final`,
      snapshotType: "final_actual",
      ...toStageLabel("final_actual"),
      createdAt:
        findLatestDate([
          ...completionEvents.map((event) => event.createdAt),
          ...tasks.map((entry) => entry.financialState?.finalActualLockedAt),
        ]) || args.projectUpdatedAt,
      totals: finalTotals,
      reliability: getStageReliability("final_actual", args.projectRollup),
      financialConfidenceScore: args.projectRollup?.financialConfidenceScore,
      relatedTaskCount: tasks.filter((entry) => entry.financialState?.financialStage === "final_actual").length,
      dominantEventLabel: summarizeDominantEventLabel({ assignmentEvents, overrunEvents, completionEvents, timelogEventCount: timelogDrivenTasks.length, snapshotType: "final_actual" }),
      departmentMovements: aggregateDepartmentMovement(tasks, "live_in_progress_forecast" as TaskFinancialStage, (entry) => getStageSnapshot(entry.financialState, "final_actual")?.grossProfit).slice(0, 3),
    },
  ];

  // The live_in_progress_forecast label is business-facing only, so map it back to the available task snapshot logic here.
  rawSnapshots[3].departmentMovements = aggregateDepartmentMovement(
    tasks,
    "assigned_artist_forecast",
    (entry) => getStageSnapshot(entry.financialState, "final_actual")?.grossProfit
  ).slice(0, 3);

  const snapshots = rawSnapshots
    .filter((snapshot) => snapshot.relatedTaskCount || snapshot.snapshotType === "initial_bid_baseline")
    .sort((a, b) => SNAPSHOT_ORDER.indexOf(a.snapshotType) - SNAPSHOT_ORDER.indexOf(b.snapshotType))
    .map<ProjectFinanceTimelineSnapshot>((snapshot, index, array) => {
      const previous = index > 0 ? array[index - 1].totals : undefined;
      return {
        ...snapshot,
        deltaFromPreviousGrossProfit:
          typeof snapshot.totals.grossProfit === "number" && typeof previous?.grossProfit === "number"
            ? roundCurrency(snapshot.totals.grossProfit - previous.grossProfit)
            : undefined,
        deltaFromPreviousMarginPct:
          typeof snapshot.totals.grossMarginPct === "number" && typeof previous?.grossMarginPct === "number"
            ? roundMargin(snapshot.totals.grossMarginPct - previous.grossMarginPct)
            : undefined,
      };
    });

  return {
    projectId: args.projectId,
    projectName: args.projectName,
    currency: args.currency,
    snapshots,
    events: buildTimelineEvents(tasks, args),
    insights: buildInsights(tasks, args.projectRollup, args.currency),
    generatedAt: now,
  };
}
